import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flag, Search } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { toast } from "sonner";

interface ClinicRow {
  id: string;
  name: string;
  created_at: string;
  is_flagged: boolean;
  flag_note: string | null;
  userCount: number;
  policyCount: number;
  lastActivity: string | null;
}

type StatusFilter = "all" | "flagged" | "inactive" | "active";

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState<ClinicRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Flag modal
  const [flagTarget, setFlagTarget] = useState<ClinicRow | null>(null);
  const [flagNote, setFlagNote] = useState("");
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    fetchClinics();
  }, []);

  async function fetchClinics() {
    setLoading(true);

    const { data: clinicData, error: clinicErr } = await supabase
      .from("clinics")
      .select("id, name, created_at, is_flagged, flag_note");

    if (clinicErr || !clinicData) {
      console.error(clinicErr);
      setLoading(false);
      return;
    }

    // Fetch counts in parallel
    const [membersRes, policiesRes] = await Promise.all([
      supabase.from("clinic_members").select("clinic_id"),
      supabase.from("policies").select("clinic_id, updated_at"),
    ]);

    const memberCounts: Record<string, number> = {};
    (membersRes.data || []).forEach((m: any) => {
      memberCounts[m.clinic_id] = (memberCounts[m.clinic_id] || 0) + 1;
    });

    const policyCounts: Record<string, number> = {};
    const lastActivityMap: Record<string, string> = {};
    (policiesRes.data || []).forEach((p: any) => {
      policyCounts[p.clinic_id] = (policyCounts[p.clinic_id] || 0) + 1;
      if (!lastActivityMap[p.clinic_id] || p.updated_at > lastActivityMap[p.clinic_id]) {
        lastActivityMap[p.clinic_id] = p.updated_at;
      }
    });

    const rows: ClinicRow[] = (clinicData as any[]).map((c) => ({
      id: c.id,
      name: c.name,
      created_at: c.created_at,
      is_flagged: c.is_flagged,
      flag_note: c.flag_note,
      userCount: memberCounts[c.id] || 0,
      policyCount: policyCounts[c.id] || 0,
      lastActivity: lastActivityMap[c.id] || null,
    }));

    setClinics(rows);
    setLoading(false);
  }

  function getStatus(c: ClinicRow): "flagged" | "inactive" | "active" {
    if (c.is_flagged) return "flagged";
    if (!c.lastActivity || differenceInDays(new Date(), new Date(c.lastActivity)) > 30) return "inactive";
    return "active";
  }

  const filtered = useMemo(() => {
    let result = clinics;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }
    if (statusFilter !== "all") {
      result = result.filter((c) => getStatus(c) === statusFilter);
    }
    // Sort: flagged first, then by last activity desc
    result = [...result].sort((a, b) => {
      if (a.is_flagged !== b.is_flagged) return a.is_flagged ? -1 : 1;
      const aTime = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
      const bTime = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
      return bTime - aTime;
    });
    return result;
  }, [clinics, search, statusFilter]);

  async function handleFlag(clinic: ClinicRow) {
    if (clinic.is_flagged) {
      // Unflag
      const { error } = await supabase
        .from("clinics")
        .update({ is_flagged: false, flag_note: null } as any)
        .eq("id", clinic.id);
      if (!error) {
        setClinics((prev) =>
          prev.map((c) => (c.id === clinic.id ? { ...c, is_flagged: false, flag_note: null } : c))
        );
        toast.success("Clinic unflagged");
      }
    } else {
      setFlagTarget(clinic);
      setFlagNote("");
    }
  }

  async function confirmFlag() {
    if (!flagTarget) return;
    setFlagging(true);

    const { error } = await supabase
      .from("clinics")
      .update({ is_flagged: true, flag_note: flagNote || null } as any)
      .eq("id", flagTarget.id);

    if (!error) {
      setClinics((prev) =>
        prev.map((c) =>
          c.id === flagTarget.id ? { ...c, is_flagged: true, flag_note: flagNote || null } : c
        )
      );

      // Send flag email
      supabase.functions.invoke("send-flag-email", {
        body: { clinic_name: flagTarget.name, flag_note: flagNote || null },
      });

      toast.success("Clinic flagged");
    }

    setFlagging(false);
    setFlagTarget(null);
  }

  function statusBadge(c: ClinicRow) {
    const s = getStatus(c);
    if (s === "flagged") return <Badge variant="destructive">🚩 Flagged</Badge>;
    if (s === "inactive") return <Badge variant="secondary" className="text-amber-600 border-amber-300">⚠️ Inactive</Badge>;
    return <Badge variant="secondary" className="text-emerald-600 border-emerald-300">✅ Active</Badge>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-[hsl(var(--header-from))] to-[hsl(var(--header-to))] px-6 py-5">
        <h1 className="text-xl font-semibold text-white">Super Admin</h1>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search clinics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="active">Active</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clinic Name</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-center">Users</TableHead>
                  <TableHead className="text-center">Policies</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No clinics found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/superadmin/clinic/${c.id}`)}
                    >
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{format(new Date(c.created_at), "MMM d, yyyy")}</TableCell>
                      <TableCell className="text-center">{c.userCount}</TableCell>
                      <TableCell className="text-center">{c.policyCount}</TableCell>
                      <TableCell>
                        {c.lastActivity ? format(new Date(c.lastActivity), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell>{statusBadge(c)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFlag(c);
                          }}
                          title={c.is_flagged ? "Unflag" : "Flag"}
                        >
                          <Flag className={`h-4 w-4 ${c.is_flagged ? "text-destructive fill-destructive" : "text-muted-foreground"}`} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Flag Modal */}
      <Dialog open={!!flagTarget} onOpenChange={(o) => !o && setFlagTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag {flagTarget?.name}</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Add a note (optional)"
            value={flagNote}
            onChange={(e) => setFlagNote(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmFlag} disabled={flagging}>
              {flagging ? "Flagging…" : "Confirm Flag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
